import { Box, Stack } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  COLORS,
  Text,
  Tooltip,
  IconButton,
  nFormatter,
} from "@asayinc/component-library";
import { useParams } from "react-router-dom";
import { useMemo, useState, useCallback } from "react";
import { Card } from "src/components/atoms/Card";
import { XAxisTick } from "src/components/atoms/XAxisTick";
import { YAxisTick } from "src/components/atoms/YAxisTick";
import {
  useProxyEventQuery,
  useProxyEventDateStatsQuery,
} from "src/store/proxyEvent";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  GraphIconType,
  IGraphDotStats,
  IGraphDotStat,
  GRAPH_DATE_FORMAT,
} from "./types";
import { CustomizedDot, MailingPopover, IActiveDotState } from "./components";
import {
  useProxyEventAdditionalMailingsQuery,
  useProxyEventVoteSolicitationsQuery,
} from "src/store/proxyEvent";

dayjs.extend(relativeTime);

export function VotingTrendStats() {
  const { id } = useParams() as { id: string };
  const { data: dateStats, isLoading: isLoadingStats } =
    useProxyEventDateStatsQuery({ id });
  const { data: proxyEvent, isLoading: isLoadingEvent } = useProxyEventQuery({
    id,
  });

  // Get additional mailings and vote solicitations for the proxy event
  const { data: additionalMailings = [], isLoading: isLoadingMailings } =
    useProxyEventAdditionalMailingsQuery({ id });
  const { data: voteSolicitations = [], isLoading: isLoadingSolicitations } =
    useProxyEventVoteSolicitationsQuery({ id });

  // Popover state for mailing hover
  const [activeMailing, setActiveMailing] = useState<IActiveDotState | null>(
    null
  );

  const isLoading =
    isLoadingStats ||
    isLoadingEvent ||
    isLoadingMailings ||
    isLoadingSolicitations;

  // Convert individual day data to cumulative
  const chartData = useMemo(() => {
    if (!dateStats || dateStats.length === 0) return [];

    return dateStats.reduce((acc, cur) => {
      const prevCustomers = acc?.[acc.length - 1]?.customers || 0;
      const prevShares = acc?.[acc.length - 1]?.shares || 0;
      acc.push({
        name: dayjs(cur.date).format(GRAPH_DATE_FORMAT),
        fullDate: cur.date,
        shares:
          prevShares +
          (cur.totalSharesVoted ? parseFloat(cur.totalSharesVoted) : 0),
        customers: prevCustomers + (cur.customerCount || 0),
      });
      return acc;
    }, [] as { name: string; fullDate: string; shares: number; customers: number }[]);
  }, [dateStats]);

  // Create mailings object mapping dates to mailing info
  const mailings = useMemo(() => {
    if (isLoading || !proxyEvent) return {};

    const result: Record<string, { name: string; type: GraphIconType }> = {};

    // Add initial mailing using approvedAt date (when the proxy event was approved)
    if (proxyEvent.approvedAt) {
      const initialMailingDate = dayjs(proxyEvent.approvedAt).format(
        GRAPH_DATE_FORMAT
      );
      result[initialMailingDate] = {
        name: "Initial mailing",
        type: GraphIconType.Additional,
      };
    }

    // Add additional mailings
    additionalMailings.forEach((mailing, idx) => {
      const dateKey = dayjs(mailing.approvedAt).format(GRAPH_DATE_FORMAT);
      result[dateKey] = {
        name: `Additional mailing ${idx + 1}`,
        type: GraphIconType.Additional,
      };
    });

    // Add vote solicitations
    voteSolicitations.forEach((solicitation, idx) => {
      const dateKey = dayjs(solicitation.scheduledSendAt).format(
        GRAPH_DATE_FORMAT
      );
      result[dateKey] = {
        name: `Voting message ${idx + 1}`,
        type: GraphIconType.ProxyMessage,
      };
    });

    return result;
  }, [isLoading, proxyEvent, additionalMailings, voteSolicitations]);

  // Calculate cumulative stats between each mailing
  const graphEventStats = useMemo(() => {
    if (!dateStats || dateStats.length === 0) return {};

    const initialDate = proxyEvent?.approvedAt
      ? dayjs(proxyEvent.approvedAt).format(GRAPH_DATE_FORMAT)
      : chartData[0]?.name;

    if (!initialDate) return {};

    const { stats } = dateStats.reduce(
      (acc, cur) => {
        const curCustomers = cur.customerCount || 0;
        const curShares = parseFloat(cur.totalSharesVoted || "0");
        const curName = dayjs(cur.date).format(GRAPH_DATE_FORMAT);
        const curNameWithYear = dayjs(cur.date).format(
          `${GRAPH_DATE_FORMAT}, YYYY`
        );

        if (mailings[curName]) {
          acc.stats[curName] = {
            shares: curShares,
            customers: curCustomers,
            range: `${acc.curDate} - ${curNameWithYear}`,
            type: mailings[curName].type,
            name: mailings[curName].name,
          };
          acc.curDate = curName;
        } else if (acc.stats[acc.curDate]) {
          (acc.stats[acc.curDate].shares as number) += curShares;
          (acc.stats[acc.curDate].customers as number) += curCustomers;
          acc.stats[acc.curDate].range = `${acc.curDate} - ${curNameWithYear}`;
        }

        return acc;
      },
      { stats: {}, curDate: initialDate } as {
        stats: IGraphDotStats;
        curDate: string;
      }
    );

    return stats;
  }, [dateStats, mailings, proxyEvent, chartData]);

  // Handle popover open/close - use useCallback to prevent re-renders
  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<SVGSVGElement>, dotStats: IGraphDotStat) => {
      setActiveMailing({
        ...dotStats,
        anchorEl: event.currentTarget,
      });
    },
    []
  );

  const handleClose = useCallback(() => {
    setActiveMailing(null);
  }, []);

  const open = Boolean(activeMailing?.anchorEl);

  // Memoize the custom dot renderer to prevent chart re-renders
  const renderCustomDot = useMemo(
    () => (
      <CustomizedDot
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleClose}
        graphEventStats={graphEventStats}
      />
    ),
    [graphEventStats, handleMouseEnter, handleClose]
  );

  if (isLoading) {
    return (
      <Card title="">
        <Text>Loading...</Text>
      </Card>
    );
  }

  if (!dateStats || dateStats.length === 0) {
    return (
      <Card title="">
        <Stack direction="row" alignItems="center" mb={2}>
          <Text variant="subtitle2" mr={1}>
            Voting trend
          </Text>
          <Tooltip title="No trending data available">
            <IconButton size="small">
              <AccessTimeIcon style={{ fontSize: "16px" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Text variant="body2" sx={{ color: "text.secondary" }}>
          No trending data available
        </Text>
      </Card>
    );
  }

  const latestStats = dateStats[dateStats.length - 1];
  const totalShares = proxyEvent?.stats?.totalShares
    ? parseFloat(proxyEvent.stats.totalShares)
    : 0;
  const totalCustomers = proxyEvent?.stats?.uniqueCustomerCount || 0;

  const votedShares = proxyEvent?.stats?.sharesVoted
    ? parseFloat(proxyEvent.stats.sharesVoted)
    : 0;
  const votedCustomers = proxyEvent?.stats?.voteCount || 0;

  // Calculate max values for Y axes
  const maxCustomers = Math.max(...chartData.map((d) => d.customers));
  const maxShares = Math.max(...chartData.map((d) => d.shares));

  // Create ticks for customer axis (right Y axis)
  const customerTicks = Array(5)
    .fill(0)
    .map((_, i) => (i * maxCustomers * 2) / 4);

  return (
    <Card title="">
      <MailingPopover
        open={open}
        handleClose={handleClose}
        activeMailing={activeMailing}
        sharesMax={maxShares}
        customerMax={maxCustomers}
      />
      <Stack>
        <Stack direction="row" alignItems="center" mb={2}>
          <Text variant="subtitle2" mr={1}>
            Voting trend
          </Text>
          <Tooltip
            title={`Last updated ${dayjs(latestStats.date).format(
              "MM/DD/YYYY"
            )} ${dayjs(latestStats.date).local().fromNow()}`}
          >
            <IconButton size="small">
              <AccessTimeIcon style={{ fontSize: "16px" }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" alignItems="flex-end" mb={2}>
          <Text variant="h5" mr={1}>
            {nFormatter(votedShares)}
          </Text>
          <Text variant="subtitle3" emphasis="medium" mr={8}>
            / {nFormatter(totalShares)} shares voted
          </Text>
          <Text variant="h5" mr={1}>
            {nFormatter(votedCustomers)}
          </Text>
          <Text variant="subtitle3" emphasis="medium">
            / {nFormatter(totalCustomers)} customers voted
          </Text>
        </Stack>

        <Box
          sx={{
            width: "100%",
            height: 400,
            mt: 2,
            svg: { overflow: "visible" },
          }}
        >
          <ResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
              key={chartData.length}
            >
              <Legend
                iconType="circle"
                iconSize={8}
                align="right"
                height={50}
                content={() => (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="flex-end"
                    sx={{ mb: 2 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: COLORS.DATA_VIZ_DARK_GREEN,
                        }}
                      />
                      <Text
                        variant="subtitle3"
                        style={{ color: COLORS.DATA_VIZ_DARK_GREEN }}
                      >
                        Shares
                      </Text>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: COLORS.DATA_VIZ_GOLD,
                        }}
                      />
                      <Text
                        variant="subtitle3"
                        style={{ color: COLORS.DATA_VIZ_GOLD }}
                      >
                        Customers
                      </Text>
                    </Stack>
                  </Stack>
                )}
                verticalAlign="top"
              />
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                padding={{ left: 20, right: 20 }}
                axisLine={false}
                tickLine={false}
                tick={<XAxisTick isLoading={false} />}
                interval={6}
                tickMargin={15}
              />
              <YAxis
                yAxisId="left"
                axisLine={false}
                tick={
                  <YAxisTick
                    color={COLORS.DATA_VIZ_DARK_GREEN}
                    anchor="end"
                    isLoading={false}
                  />
                }
                tickLine={false}
                tickMargin={12}
                width={50}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickMargin={12}
                type="number"
                tickCount={5}
                allowDataOverflow
                ticks={customerTicks}
                tickLine={false}
                tick={
                  <YAxisTick
                    color={COLORS.DATA_VIZ_GOLD}
                    anchor="start"
                    isLoading={false}
                    rightTick={true}
                  />
                }
                axisLine={false}
                domain={[0, maxCustomers * 2]}
              />
              <RechartsTooltip
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                content={({ active, payload }: any) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <Box
                        sx={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: 1,
                          p: 2,
                        }}
                      >
                        <Text variant="caption" sx={{ fontWeight: 600, mb: 1 }}>
                          As of {dayjs(data.fullDate).format("MMM D")}
                        </Text>
                        <Box>
                          <Text
                            variant="caption"
                            sx={{ color: COLORS.DATA_VIZ_DARK_GREEN }}
                          >
                            • {nFormatter(data.shares)} shares
                          </Text>
                        </Box>
                        <Box>
                          <Text
                            variant="caption"
                            sx={{ color: COLORS.DATA_VIZ_GOLD }}
                          >
                            • {nFormatter(data.customers)} customers
                          </Text>
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                }}
                position={{ y: -39 }}
                wrapperStyle={{ left: -90 }}
              />
              <Line
                yAxisId="right"
                strokeWidth={3}
                dot={false}
                type="monotone"
                dataKey="customers"
                stroke={COLORS.DATA_VIZ_GOLD}
                isAnimationActive={false}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="shares"
                stroke={COLORS.DATA_VIZ_DARK_GREEN}
                strokeWidth={3}
                dot={renderCustomDot}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </Card>
  );
}
